import { isDevMode } from '@angular/core';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { BehaviorSubject, combineLatest, of, ReplaySubject, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { BuilderCode, FiltreBuilder, LineOperator, LineThemeCode } from '../filtre-builders/filtre-builder.model';

export class FiltreLine {
  themeCode: LineThemeCode;
  builderCode: BuilderCode;
  order: number | null = null;
  isButton = false;

  private icon?: string;
  private destroy$ = new Subject<void>();
  private options$ = new ReplaySubject<OuiSelectOption[] | null>(1);

  private _builder: FiltreBuilder | null = null;
  private _operator: LineOperator | null = null;
  private _value: any | null = null;

  private value$ = new BehaviorSubject<any | null>(null);
  private operator$ = new BehaviorSubject<LineOperator | null>(null);

  private optionsSnapshot: OuiSelectOption[] | null = null;

  filtreValueLabel$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  get builder(): FiltreBuilder | null {
    return this._builder;
  }

  set builder(newBuilder: FiltreBuilder | null) {
    this.destroy$.next();
    this._builder = newBuilder;

    if (newBuilder) {
      if (newBuilder.isButton) {
        this.setIcon(this.builderCode);
        this.isButton = true;
        this.operator = newBuilder.operatorOptions[0].value ?? null;
      }

      newBuilder.options?.pipe(takeUntil(this.destroy$)).subscribe(options => {
        this.options$.next(options);
      });
    } else {
      this.options$.next(null);
      this.isButton = false;
      this.operator = null;
      this.value = null;
    }
  }

  get operator(): LineOperator | null {
    return this._operator;
  }
  set operator(op: LineOperator | null) {
    this._operator = op;
    this.operator$.next(op);
  }

  get value(): any | null {
    return this._value;
  }
  set value(v: any | null) {
    this._value = this.cleanValue(v);
    this.value$.next(this._value);
  }

  constructor(themeCode: LineThemeCode, itemCode: BuilderCode, builder?: FiltreBuilder | null, order?: number) {
    this.themeCode = themeCode;
    this.builderCode = itemCode;
    this.builder = builder ?? null;
    if (order !== undefined) {
      this.order = order;
    }
    // combine options + value + operator changes
    combineLatest([
      this.options$.pipe(
        startWith(null),
        tap(o => (this.optionsSnapshot = o))
      ),
      this.value$,
      this.operator$,
    ])
      .pipe(
        switchMap(([options, value, operator]) => {
          if (!this.builder || value === null || operator === null) {
            return of(null);
          }
          return this.computeLabel(options, value, operator);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(label => {
        this.filtreValueLabel$.next(label);
      });
  }

  private computeLabel(options: OuiSelectOption[] | null, value: any, operator: LineOperator) {
    if (!this.builder) return of(null);
    const builder = this.builder!;
    let valueLabel = '';

    // trouve une option même si elle est cachée dans des "children" (option groupée)
    const findOption = (val: any) => {
      for (const opt of options || []) {
        if (opt.children) {
          const foundInChildren = opt.children.find((c: any) =>
            Array.isArray(val) ? JSON.stringify(c.value) === JSON.stringify(val) : c.value === val
          );
          if (foundInChildren) return foundInChildren;
        } else {
          const match = Array.isArray(val) ? JSON.stringify(opt.value) === JSON.stringify(val) : opt.value === val;
          if (match) return opt;
        }
      }
      return null;
    };

    if (Array.isArray(value)) {
      if (options && options.length > 0) {
        valueLabel = value
          .map((v: any) => {
            const opt = findOption(v);
            return opt?.label ?? String(v);
          })
          .join(', ');
      } else {
        valueLabel = value.join(', ');
      }
    } else {
      if (options && options.length > 0) {
        const opt = findOption(value);
        valueLabel = opt?.label ?? String(value);
      } else {
        valueLabel = String(value);
      }
    }
    switch (builder.inputType) {
      case 'InputEuro':
        valueLabel += ' €';
        break;
      case 'InputPercent':
        valueLabel += ' %';
        break;
      case 'Age':
        valueLabel += ' ans';
        break;
      case 'Date':
      case 'DateTime':
        try {
          const date = new Date(valueLabel);
          if (!isNaN(date.getTime())) {
            valueLabel = date.toLocaleDateString('fr-FR');
          }
        } catch {
          if (isDevMode()) {
            console.error(`Date invalid: ${valueLabel}`);
          }
        }
        break;
    }
    const operatorLibelle = builder.getOperatorLibelle(operator, true);
    const builderLibelle = FiltreBuilder.getBuilderLibelle(this.builderCode);

    return of(`${builderLibelle} ${operatorLibelle} ${valueLabel}`);
  }

  setIcon(builderCode: BuilderCode) {
    switch (builderCode) {
      case 'INVESTISSEUR_ORIGINE_CONTACT':
        this.icon = 'folder_shared';
        break;
      case 'INVESTISSEUR_STATUT':
        this.icon = 'account_balance_wallet';
        break;
      case 'INVESTISSEUR_ETIQUETTE':
        this.icon = 'sell';
        break;
      case 'REC_STATUT':
        this.icon = 'list_alt';
        break;
      case 'QPI_STATUT':
        this.icon = 'show_chart';
        break;
      case 'PIECEIDENTITE_STATUT':
        this.icon = 'id_card';
        break;
      case 'RELATION_SECONDAIRE':
        this.icon = 'group';
        break;
      case 'DER_STATUT':
        this.icon = 'verified_user';
        break;
      case 'MYSTELLIUM_STATUS':
        this.icon = 'account_box';
        break;
      default:
        this.icon = 'person_play';
    }
  }

  getIcon(): string {
    return this.icon ?? 'person_play';
  }

  withValue(value: any | null): FiltreLine {
    const newLine = new FiltreLine(this.themeCode, this.builderCode, this.builder);
    newLine.order = this.order;
    newLine.operator = this.operator;
    newLine.value = value;
    return newLine;
  }

  // Nettoie la valeur en gardant les éléments qui sont dans les options disponibles
  private cleanValue(value: any): any {
    const options = this.optionsSnapshot;
    if (!Array.isArray(value) || !options || options.length === 0) {
      return value;
    }

    const validOptionValues: any[] = [];

    for (const opt of options) {
      if (opt.children && opt.children.length > 0) {
        //check pour les options groupées
        validOptionValues.push(...opt.children.map((c: any) => c.value));
      } else if (opt.value !== undefined) {
        validOptionValues.push(opt.value);
      }
    }

    if (value.every(v => Array.isArray(v))) {
      const sameArray = (a: string[], b: string[]) => a.length === b.length && a.every(v => b.includes(v));

      return value.filter(savedGroup =>
        options.some((option: any) => {
          if (option.children) {
            //check pour les options groupées
            return option.children.some((c: any) => sameArray(c.value, savedGroup));
          }
          return sameArray(option.value, savedGroup);
        })
      );
    }

    return value.filter((v: any) => validOptionValues.includes(v));
  }
}
